import {
  AVAILABILITY_LABELS,
  EMPLOYMENT_LABELS,
  SENIORITY_LABELS,
  label,
} from '@remote-medium/shared';
import { CircleAlert, FileText, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { API_ORIGIN } from '../../../lib/client.ts';
import { Card, Chips, Facts, SectionHeader } from '../../../lib/ui.tsx';
import type { Candidate, CandidateContext } from './context.ts';

function Missing({ children = 'None listed' }: { children?: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
      <CircleAlert aria-hidden="true" className="size-4" />
      {children}
    </span>
  );
}

function Group({ title }: { title: string }) {
  return (
    <h3 className="m-0 border-b border-border pb-2 text-xs font-medium text-muted-foreground">
      {title}
    </h3>
  );
}

const compact = (n: number) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
    .format(n)
    .toLowerCase();

/**
 * Where the candidate's minimum sits against open roles in the families they
 * target, same currency only. Null when there's nothing to compare with.
 */
function salaryContext(c: Candidate, roles: CandidateContext['openRoles']): string | null {
  if (c.salaryMin === null || !c.currency) return null;
  const families = [...new Set(c.roleTargets.map((t) => t.family))];
  const comparable = roles.filter(
    (r) =>
      families.includes(r.roleTypeFamily) &&
      r.currency === c.currency &&
      (r.salaryMin !== null || r.salaryMax !== null),
  );
  if (!comparable.length) return null;
  const what = families.length === 1 ? `open ${families[0]} role` : 'open role in their targets';
  const min = c.salaryMin;
  if (comparable.every((r) => r.salaryMin !== null && min < r.salaryMin))
    return `Below every ${what}'s range`;
  if (comparable.every((r) => r.salaryMax !== null && min > r.salaryMax))
    return `Above every ${what}'s range`;
  const within = comparable.filter(
    (r) =>
      (r.salaryMin === null || min >= r.salaryMin) && (r.salaryMax === null || min <= r.salaryMax),
  ).length;
  return `Within the range of ${within} of ${comparable.length} comparable open roles`;
}

export function ProfileCard({
  c,
  openRoles,
}: {
  c: Candidate;
  openRoles: CandidateContext['openRoles'];
}) {
  // Only gaps that lower scores: empty industries score 0 on roles with an
  // industry, empty skills score 0 on skills. A missing salary skips the rule
  // and a missing CV never affects ranking.
  const gaps = [
    !c.skills.length && 'skills',
    !c.industriesWorkedIn.length && 'industries worked in',
    !c.industriesInterestedIn.length && 'industries of interest',
  ].filter((g): g is string => !!g);
  const first = c.fullName.split(' ')[0];
  const salaryNote = salaryContext(c, openRoles);

  return (
    <Card>
      <SectionHeader title="Profile" />

      {gaps.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-subtle px-4 py-3 text-sm text-warning-subtle-foreground">
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">
              {gaps.length === 1 ? '1 field is' : `${gaps.length} fields are`} empty, and{' '}
              {gaps.length === 1 ? 'it costs' : 'they cost'} points in ranking
            </span>
            <span>
              No {gaps.join(' or ')} means those rules score 0 against every role that uses them.
              Ask {first} to add them, or add them from the screening call.
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Group title="Looking for" />
        <Facts
          items={[
            [
              'Targets',
              c.roleTargets.length ? (
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {c.roleTargets.map((t) => (
                    <li key={t.roleTypeId}>
                      {t.family} · {t.name}
                      <span className="ml-3 text-muted-foreground">
                        {label(SENIORITY_LABELS, t.seniority)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <Missing />
              ),
            ],
            [
              'Employment',
              c.employmentTypes.length ? (
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {c.employmentTypes.map((e) => (
                    <span key={e}>{label(EMPLOYMENT_LABELS, e)}</span>
                  ))}
                </span>
              ) : (
                <Missing />
              ),
            ],
            [
              'Minimum salary',
              c.salaryMin !== null ? (
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  <span>
                    {c.currency ? `${c.currency} ` : ''}
                    {compact(c.salaryMin)} / year
                  </span>
                  {salaryNote && <span className="text-muted-foreground">{salaryNote}</span>}
                </span>
              ) : (
                <Missing>Not given</Missing>
              ),
            ],
            [
              'Availability',
              c.availability ? (
                label(AVAILABILITY_LABELS, c.availability)
              ) : (
                <Missing>Not given</Missing>
              ),
            ],
          ]}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Group title="Experience" />
        <Facts
          items={[
            [
              'Skills',
              c.skills.length ? (
                <Chips
                  items={c.skills.map((s) =>
                    s.years !== null ? `${s.name} · ${s.years}y` : s.name,
                  )}
                />
              ) : (
                <Missing />
              ),
            ],
            [
              'Worked in',
              c.industriesWorkedIn.length ? (
                <Chips items={c.industriesWorkedIn.map((i) => i.name)} />
              ) : (
                <Missing />
              ),
            ],
            [
              'Interested in',
              c.industriesInterestedIn.length ? (
                <Chips items={c.industriesInterestedIn.map((i) => i.name)} />
              ) : (
                <Missing />
              ),
            ],
            [
              'CV',
              c.hasCv ? (
                <a
                  href={`${API_ORIGIN}/admin/candidates/${c.id}/cv`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-foreground"
                >
                  <FileText aria-hidden="true" className="size-4" />
                  Open CV
                </a>
              ) : (
                <Missing>Not uploaded</Missing>
              ),
            ],
          ]}
        />
      </div>
    </Card>
  );
}
